import { styled } from '@mui/material';
import classNames from 'classnames';
import * as d3 from 'd3';
import { select } from 'd3';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ActiveCase, CaseRotationStatus } from '#entities';
import {
  AIRCRAFT_SVG_SIZE,
  ENOPOrganType,
  getDashMeta,
  NavigationOutlinedIcon,
  normalizeDashArray,
  ORGAN_COLORS,
  solveTriangleCircle,
  type Point,
} from '#shared';

import { EZLayers, useAircrafts, ZLayer } from '../../data-context';
import { useGeoMap } from '../../GeoMap';
import {
  expandActiveStatuses,
  getCurve,
  parseDonorReference,
  parseDonorReferencePrimary,
} from '../../helpers';

import { AircraftOnFlight } from './AircraftOnFlight';
import { StyledDot } from './StyledDot';

const LOCATION_POINTER_SIZE = 15;

const Root = styled('g')(() => ({}));

const dashSlideKeyframes = {
  '@keyframes dashSlide': {
    from: { strokeDashoffset: 0 },
    to: { strokeDashoffset: 'var(--dashOffsetEnd, -1000px)' }, // seamless cycle
  },
};

const StyledCurvePath = styled('path')({
  fill: 'none',
  strokeWidth: 2,
  strokeLinecap: 'round',
  vectorEffect: 'non-scaling-stroke',
  ...dashSlideKeyframes,
});

type StyledCurveBaseProps = {
  animateDash?: boolean;
  dashDuration?: string;
  baseOpacity?: number;
  dashOffsetEnd?: string;
};

const excludedProps: PropertyKey[] = [
  'animateDash',
  'dashDuration',
  'baseOpacity',
  'dashOffsetEnd',
];

export const StyledCurveBase = styled(StyledCurvePath, {
  shouldForwardProp: prop => !excludedProps.includes(String(prop)),
})<StyledCurveBaseProps>(
  ({
    animateDash,
    dashDuration,
    baseOpacity,
    dashOffsetEnd,
    theme: { mixins, palette },
  }) => ({
    ...mixins.severityStyles(palette),
    opacity: baseOpacity ?? 1,
    transition: 'opacity 1s ease',
    ...(dashOffsetEnd ? { ['--dashOffsetEnd']: dashOffsetEnd } : {}),
    ...(animateDash
      ? {
          animationName: 'dashSlide',
          animationTimingFunction: 'linear',
          animationIterationCount: 'infinite',
          animationDuration: dashDuration ?? '2.5s',
          strokeDashoffset: 0,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }
      : {
          animation: 'none',
          strokeDashoffset: 0,
        }),
  }),
);

const StyledCurveOverlay = styled(StyledCurvePath)({});

const StyledProgressPointer = styled(NavigationOutlinedIcon, {
  shouldForwardProp: prop => prop !== 'organName',
})<{
  organName?: string;
}>(({ organName }) => ({
  color: 'var(--primaryColor)',
  transformOrigin: 'center',
  transition: 'transform 0.3s ease-in-out',
  ...(organName && {
    [`&.organ-type-${organName}`]: {
      color: 'var(--organColor)',
    },
  }),
}));

interface CaseLayerProps {
  caseItem: ActiveCase;
  activeRotationStatus?: CaseRotationStatus;
  isRotationActive?: boolean;
  lastUpdateTime: Date | null;
  showAircraft: boolean;
  onAircraftSelect?: (aircraftId: number) => void;
}
type LinkData = {
  source: Point;
  target: Point;
  organ: ENOPOrganType | null;
  id: string;
};
export const CaseLayer = ({
  caseItem,
  activeRotationStatus,
  isRotationActive,
  lastUpdateTime,
  showAircraft,
  onAircraftSelect,
}: CaseLayerProps) => {
  const isTmdxAviation = !!caseItem.flightIds?.length;
  const {
    id,
    organ,
    mapProgress,
    label,
    donorHospital,
    recipientHospital,
    points,
  } = caseItem;

  const { organShort, unosId } = parseDonorReferencePrimary(label);
  const aircraftElWrapperRef = useRef<SVGGElement | null>(null);

  const aircrafts = useAircrafts();
  const aircraft = useMemo(
    () =>
      showAircraft &&
      aircrafts.find(({ actualLabel, isOnFlight }) => {
        const allParsed = parseDonorReference(actualLabel);
        return (
          allParsed.some(
            p => p.organShort === organShort && p.unosId === unosId,
          ) && isOnFlight
        );
      }),
    [aircrafts, organShort, showAircraft, unosId],
  );
  const curveRef = useRef<SVGPathElement | null>(null);
  const {
    projectionRef,
    onChangeProgress,
    onChangeEnd,
    interactionLayerRef,
    contextId,
  } = useGeoMap();

  const gradientId = `case-curve-gradient-${id}-${contextId}`;
  const progressRef = useRef(mapProgress);

  const [locationPointerBasePosition, setLocationPointerBasePosition] =
    useState<{ x: number; y: number; heading: number }>();

  const [linkData, setLinkData] = useState<LinkData | null>(null);
  const getAircraftLinkData = useCallback(() => {
    const svgEl = interactionLayerRef.current as SVGSVGElement;
    if (
      !(
        aircraft &&
        aircraftElWrapperRef.current &&
        curveRef.current &&
        svgEl?.tagName === 'svg'
      )
    ) {
      return null;
    }

    const { organShort, unosId, organ } = parseDonorReferencePrimary(
      aircraft.actualLabel,
    );
    const caseId = organShort && unosId && `${unosId}-${organShort}`;
    if (!caseId) {
      return null;
    }

    // show line only if plane is visible in this exact SVG
    const svgRect = svgEl.getBoundingClientRect();
    const planeRect = aircraftElWrapperRef.current.getBoundingClientRect();

    const aircraftVisible =
      planeRect.width > 0 &&
      planeRect.height > 0 &&
      planeRect.right > svgRect.left &&
      planeRect.left < svgRect.right &&
      planeRect.bottom > svgRect.top &&
      planeRect.top < svgRect.bottom;
    if (!aircraftVisible) {
      return null;
    }

    // geometry
    const A = curveRef.current.getPointAtLength(0);
    const B = curveRef.current.getPointAtLength(
      curveRef.current.getTotalLength(),
    );
    const mid = curveRef.current.getPointAtLength(
      curveRef.current.getTotalLength() / 2,
    );

    // precise plane center using the group's CTM (prevents any offsets)
    const gCTM = aircraftElWrapperRef.current.getCTM() as DOMMatrix;

    const p = svgEl.createSVGPoint();
    p.x = AIRCRAFT_SVG_SIZE / 2;
    p.y = AIRCRAFT_SVG_SIZE / 2;
    const center = p.matrixTransform(gCTM);
    const xD = center.x;
    const yD = center.y;

    const closest = solveTriangleCircle(
      { x: A.x, y: A.y },
      { x: B.x, y: B.y },
      { x: mid.x, y: mid.y },
      { x: xD, y: yD },
    )?.closestPoint;

    if (closest) {
      return {
        source: [xD, yD],
        target: [closest.x, closest.y],
        id: `link-${caseId}`,
        organ,
      } as LinkData;
    }
    return null;
  }, [aircraft, interactionLayerRef]);

  const refreshPosition = useCallback(() => {
    const el = curveRef.current;
    if (!el) {
      return;
    }

    try {
      const total = el.getTotalLength();
      if (!total) {
        return;
      }

      const fraction = progressRef.current / 100;
      const length = total * fraction;

      const delta = 1;
      const l1 = Math.max(0, Math.min(total, length - delta));
      const l2 = Math.max(0, Math.min(total, length + delta));

      const p1 = el.getPointAtLength(l1);
      const p2 = el.getPointAtLength(l2);

      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);

      setLocationPointerBasePosition({ x: p1.x, y: p1.y, heading: angle + 90 });

      const r = getAircraftLinkData();
      setLinkData(r);
    } catch (err) {
      console.error('[CaseLayer] Error computing pointer position', err);
    }
  }, [getAircraftLinkData]);

  const setCurveRef = useCallback(
    (node: SVGPathElement | null) => {
      if (node) {
        curveRef.current = node;
        requestAnimationFrame(refreshPosition);
      }
    },
    [refreshPosition],
  );

  useEffect(() => {
    progressRef.current = mapProgress;
  }, [mapProgress]);

  useEffect(() => {
    const curveElement = curveRef.current;
    if (!curveElement) {
      return;
    }

    // Initial calculation on mapProgress change
    requestAnimationFrame(refreshPosition);

    // Recalculate when path attributes change
    const observer = new MutationObserver(() => {
      requestAnimationFrame(refreshPosition);
    });

    observer.observe(curveElement, { attributes: true });
    return () => observer.disconnect();
  }, [mapProgress, refreshPosition]);

  const isThirdPartyAviation = useMemo(
    () => points?.some(i => i.type === 'airNonMao'),
    [points],
  );

  const [[pointA, pointB], setPoints] = useState<[Point | null, Point | null]>([
    null,
    null,
  ]);

  const curve = useMemo(() => {
    if (!pointA || !pointB) {
      return null;
    }
    return getCurve(
      pointA,
      pointB,
      `${String(lastUpdateTime ?? '')}|${caseItem.id}`,
    );
  }, [pointA, pointB, lastUpdateTime, caseItem.id]);

  useEffect(() => {
    const animateFrame = () => {
      const a = donorHospital?.lonLat
        ? projectionRef.current(donorHospital.lonLat)
        : null;
      const b = recipientHospital?.lonLat
        ? projectionRef.current(recipientHospital.lonLat)
        : null;
      setPoints([a as Point, b as Point]);
    };
    const unsubscribeChangeProgress = onChangeProgress(animateFrame);
    const unsubscribeChangeEnd = onChangeEnd(animateFrame);
    animateFrame();
    return () => {
      unsubscribeChangeProgress();
      unsubscribeChangeEnd();
    };
  }, [
    donorHospital,
    recipientHospital,
    projectionRef,
    onChangeProgress,
    onChangeEnd,
  ]);

  const strokeColor =
    (organ?.name && ORGAN_COLORS[organ.name]) || 'var(--primaryColor)';

  const dash = useMemo<string | undefined>(() => {
    if (isThirdPartyAviation && !isTmdxAviation) {
      return '4 4';
    }
    if (isTmdxAviation) {
      return undefined;
    } // solid
    return '8 8';
  }, [isThirdPartyAviation, isTmdxAviation]);

  const isSolidLane = !dash;
  const rotationOn = Boolean(isRotationActive && activeRotationStatus);
  const mapNS = useId().replace(/:/g, '');
  const isCurrent =
    rotationOn &&
    expandActiveStatuses(activeRotationStatus).has(caseItem.status);

  const nodesOpacity = rotationOn ? (isCurrent ? 1.0 : 0.2) : 1.0;
  const defaultBaseOpacity = isSolidLane ? 0.4 : 0.6;
  const overlayOpacity = rotationOn ? (isCurrent ? 1.0 : 0.0) : 1.0;
  const baseOpacity = rotationOn
    ? isCurrent
      ? defaultBaseOpacity
      : 0.2
    : defaultBaseOpacity;

  useEffect(() => {
    if (!(pointA && pointB) || !isSolidLane) {
      return;
    }

    // Only animate when overlay should be visible & moving:
    const shouldAnimate = !rotationOn || isCurrent;
    const svg = interactionLayerRef.current as SVGSVGElement | null;
    if (!svg) {
      return;
    }

    // Clear any prior gradient first
    d3.select(`#${gradientId}`).remove();

    if (!shouldAnimate) {
      // When not animating (non-current during rotation), keep it removed.
      return;
    }

    const defsSel = select(svg).select('defs').empty()
      ? select(svg).append('defs')
      : select(svg).select('defs');

    const [x1, y1] = pointA;
    const [x2, y2] = pointB;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const overshoot = 0.53;

    const grad = defsSel
      .append('linearGradient')
      .attr('id', gradientId)
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', x1)
      .attr('y1', y1)
      .attr('x2', x2)
      .attr('y2', y2);

    grad
      .append('stop')
      .attr('offset', '0%')
      .style('stop-color', strokeColor)
      .attr('stop-opacity', 0);
    grad
      .append('stop')
      .attr('offset', '22.4774%')
      .style('stop-color', strokeColor)
      .attr('stop-opacity', 1);
    grad
      .append('stop')
      .attr('offset', '75%')
      .style('stop-color', strokeColor)
      .attr('stop-opacity', 1);
    grad
      .append('stop')
      .attr('offset', '100%')
      .style('stop-color', strokeColor)
      .attr('stop-opacity', 0);

    grad
      .append('animate')
      .attr('attributeName', 'x1')
      .attr(
        'values',
        `${x1 - dx * overshoot}; ${x2 + dx * overshoot}; ${x2 + dx * overshoot}`,
      )
      .attr('dur', '2.5s')
      .attr('keyTimes', '0; 0.8; 1')
      .attr('repeatCount', 'indefinite');

    grad
      .append('animate')
      .attr('attributeName', 'y1')
      .attr(
        'values',
        `${y1 - dy * overshoot}; ${y2 + dy * overshoot}; ${y2 + dy * overshoot}`,
      )
      .attr('dur', '2.5s')
      .attr('keyTimes', '0; 0.8; 1')
      .attr('repeatCount', 'indefinite');

    grad
      .append('animate')
      .attr('attributeName', 'x2')
      .attr('values', `${x1}; ${x2}; ${x2}`)
      .attr('dur', '2.5s')
      .attr('keyTimes', '0; 0.8; 1')
      .attr('repeatCount', 'indefinite');

    grad
      .append('animate')
      .attr('attributeName', 'y2')
      .attr('values', `${y1}; ${y2}; ${y2}`)
      .attr('dur', '2.5s')
      .attr('keyTimes', '0; 0.8; 1')
      .attr('repeatCount', 'indefinite');

    grad
      .append('animate')
      .attr('attributeName', 'opacity')
      .attr('values', '1;0;0')
      .attr('dur', '2.5s')
      .attr('keyTimes', '0; 0.8; 1')
      .attr('repeatCount', 'indefinite');

    return () => {
      d3.select(`#${gradientId}`).remove();
    };
  }, [
    pointA,
    pointB,
    interactionLayerRef,
    gradientId,
    isSolidLane,
    strokeColor,
    rotationOn,
    isCurrent,
  ]);

  useEffect(() => {
    const u1 = onChangeProgress(() => requestAnimationFrame(refreshPosition));
    const u2 = onChangeEnd(() => requestAnimationFrame(refreshPosition));
    return () => {
      u1();
      u2();
    };
  }, [onChangeProgress, onChangeEnd, refreshPosition]);

  useEffect(() => {
    if (!curve) {
      return;
    }
    requestAnimationFrame(refreshPosition);
  }, [curve, refreshPosition]);

  if (!(pointA && pointB && curve)) {
    return null;
  }
  const isDashed = !!dash;
  const shouldAnimateDash = isDashed && (!rotationOn || isCurrent);
  const normalizedDash = normalizeDashArray(dash);
  const { period, type } = getDashMeta(normalizedDash);

  // decide dash animation speed based on type (faster for dots, slower for dashes)
  const dashDuration =
    type === 'dots'
      ? `${Math.max(0.4, (period ?? 4) / 120)}s` // faster
      : `${Math.max(1.0, (period ?? 10) / 60)}s`; // slower

  const flightGradientId = linkData
    ? `${mapNS}-flight-gradient-${linkData.id}`
    : null;
  return (
    <Root>
      <defs>
        {linkData && (
          <linearGradient
            id={flightGradientId as string}
            gradientUnits="userSpaceOnUse"
            className={`organ-type-${linkData.organ}`}
            x1={linkData.source[0]}
            y1={linkData.source[1]}
            x2={linkData.target[0]}
            y2={linkData.target[1]}
          >
            <stop offset="0%" stopColor="var(--organColor)" stopOpacity={0.8} />
            <stop offset="100%" stopColor="var(--organColor)" stopOpacity={0} />
          </linearGradient>
        )}
      </defs>
      {/* BASE SOLID LINE */}
      <ZLayer zIndex={EZLayers.Background0}>
        {linkData && (
          <line
            className={`case-curve-aircraft-on-flight-link organ-type-${linkData.organ}`}
            stroke={`url(#${flightGradientId})`}
            strokeWidth={2}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            pointerEvents="none"
            filter="brightness(0.55) saturate(0.8) drop-shadow(0 32px 2px rgba(0, 0, 0, 0.3))"
            x1={linkData.source[0]}
            y1={linkData.source[1]}
            x2={linkData.target[0]}
            y2={linkData.target[1]}
          />
        )}
        <StyledCurveBase
          ref={setCurveRef}
          className={classNames('case-curve', {
            [`case-${unosId}-${organShort}`]: organShort && unosId,
          })}
          id={`${id}-${contextId}-curve-base`}
          d={curve}
          stroke={strokeColor} // Safari-friendly
          strokeDasharray={normalizedDash}
          baseOpacity={baseOpacity}
          animateDash={shouldAnimateDash}
          dashDuration={dashDuration}
          dashOffsetEnd={`-${period ?? 100}px`} // ensures seamless cycle
        />
      </ZLayer>
      {/* ANIMATED GRADIENT OVERLAY */}
      <ZLayer zIndex={EZLayers.Background}>
        {isSolidLane && (
          <StyledCurveOverlay
            id={`${id}-${contextId}-curve-overlay`}
            d={curve}
            style={{
              stroke: `url(#${gradientId})`,
              opacity: overlayOpacity,
              transition: 'opacity 300ms ease',
              pointerEvents: 'none',
            }}
          />
        )}
        <g
          transform={`translate(${pointA[0]}, ${pointA[1]})`}
          style={{ opacity: nodesOpacity, transition: 'opacity 1s ease' }}
        >
          <StyledDot organType={organ.name} />
        </g>
        <g
          transform={`translate(${pointB[0]}, ${pointB[1]})`}
          style={{ opacity: nodesOpacity, transition: 'opacity 1s ease' }}
        >
          <StyledDot organType={organ.name} />
        </g>
      </ZLayer>
      {/* MOVING POINTER */}
      <ZLayer zIndex={EZLayers.Default}>
        {!!locationPointerBasePosition && (
          <g
            transform={`translate(${locationPointerBasePosition.x}, ${locationPointerBasePosition.y}) rotate(${locationPointerBasePosition.heading})`}
            style={{
              opacity: nodesOpacity,
              transition: 'opacity 1s ease',
            }}
          >
            <StyledProgressPointer
              className={classNames({
                [`organ-type-${organ.name}`]: organ.name,
              })}
              organName={organ.name}
              width={LOCATION_POINTER_SIZE}
              height={LOCATION_POINTER_SIZE}
              x={-LOCATION_POINTER_SIZE / 2}
              y={-LOCATION_POINTER_SIZE / 2}
              style={{ color: strokeColor }}
            />
          </g>
        )}

        {aircraft && showAircraft && (
          <AircraftOnFlight
            ref={aircraftElWrapperRef}
            aircraftData={aircraft}
            onAircraftSelect={onAircraftSelect}
          />
        )}
      </ZLayer>
    </Root>
  );
};
